import { z } from "zod";

import { logAndThrow } from "@/src/lib/logging/logger";
import { enrichedContentUnionSchema } from "@/src/models/blocks/enriched-content";
import { Classification } from "@/src/models/constants/classifications";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { intakeExtractionAnnotationsMetadataSchema } from "@/src/models/intake-annotation";

export enum SectionStatus {
  PROCESSED = "processed",
  FAILED = "failed",
  EXTRACTING = "extracting",
  INVALID = "invalid",
  NO_EXTRACT = "no_extract", // No OCR was performed on the section
}

export const DEFAULT_CLASSIFICATIONS_TO_EXTRACT = [
  Classification.INTAKE,
  Classification.REFERRAL,
  Classification.REFERER_INFORMATION,
  Classification.RESPONSE_TO_VH_REQUEST,
];

export const sectionMetadataSchema = z
  .object({
    extractionAnnotations: intakeExtractionAnnotationsMetadataSchema.optional(),
    /** Set when this section's reviewed content was merged into an existing task instead of spawning a new one. */
    mergedIntoTaskId: z.string().optional(),
  })
  .passthrough();

export const boundingBoxSchema = z.object({
  id: z.string(),
  bbox: z.object({
    xywh: z.array(z.number()),
  }),
  page: z.number(),
  content: z.string(),
});

export const extractionMetadataSchema = z.object({
  bboxes: z.array(boundingBoxSchema),
  confidence: z.string(),
  annotation_ids: z.array(z.string()),
});

export const sectionSchema = z.object({
  pathToFile: z.string(),
  filename: z.string(),
  contentType: z.string(),
  checksum: z.string(),
  status: z.nativeEnum(SectionStatus),
  classification: z.nativeEnum(Classification),
  fileSize: z.number(),
  processedContent: z.any().nullable(),
  processedContentMetadata: z.any().nullable(),
  enrichedContent: enrichedContentUnionSchema,
  reviewedContent: z.any().nullable(),
  fileId: z.string().nullable(),
  vlmProcessedContent: z.any().nullable(),
  geminiProcessedContent: z.any().nullable(),
  pages: z.array(z.number()),
  ehrDocumentId: z.string().nullable(),
  ehrDocumentType: z.string().nullable(),
  metadata: sectionMetadataSchema.nullable(),
  intakeClassification: z.nativeEnum(Classification).nullable(),
  uploadClassification: z.nativeEnum(Classification).nullable(),
  intakePathToFile: z.string().nullable(),
  uploadPathToFile: z.string().nullable(),
  intakeSectionId: z.string().nullable(),
  uploadSectionId: z.string().nullable(),
  intakePages: z.array(z.number()).nullable(),
  uploadPages: z.array(z.number()).nullable(),
  isDuplicate: z.boolean().default(false),
  hasMultiplePatients: z.boolean().default(false),
  completedAt: z.string().nullable(),
  extractionGroundTruth: z.any().nullable(),
});

export type Section = z.infer<typeof sectionSchema> & DatabaseDefaults;
export type ExtractionMetadata = z.infer<typeof extractionMetadataSchema>;
export type BoundingBox = z.infer<typeof boundingBoxSchema>;

export function requireUploadClassification(section: Section): Classification {
  if (section.uploadClassification) return section.uploadClassification;

  logAndThrow("Section is missing required uploadClassification", {
    groupId: section.groupId,
    sectionId: section.id,
    classification: section.classification,
  });
}
